import { Role } from "utils/saastypes";

/**
 * Shaping and helpers for the access-keys screen, kept out of the component so the parts that are
 * easy to get subtly wrong can be tested without rendering.
 *
 * Access keys are the machine-to-machine credential ported from V2 in WP-4. Three behaviours of
 * that backend drive most of what this file does:
 *
 *   - The token is returned exactly once, by the create call. Only a BCrypt hash of the underlying
 *     secret is stored, so a caller who loses the token has to create another key.
 *   - The signing secret is configured per environment, and is deliberately unset on beta. Creation
 *     there fails with a specific 400 rather than a server error, and that is a state to explain
 *     rather than an error to report.
 *   - `expires_at` is recorded but nothing enforces it. Revoking is what actually stops a key
 *     working, so the UI must not imply that an expiry date will.
 */

export interface AccessKey {
  id: string;
  organizationId?: string;
  createdAt?: string;
  createdBy?: string;
  expiresAt?: number;
  active?: boolean;
  roles?: string[];
}

/** V2's default, and the backend's: 315,360,000 seconds, exactly ten 365-day years. */
export const DEFAULT_EXPIRATION_SECONDS = 315360000;

export const EXPIRY_OPTIONS = [
  { label: "30 days", seconds: 60 * 60 * 24 * 30 },
  { label: "90 days", seconds: 60 * 60 * 24 * 90 },
  { label: "1 year", seconds: 60 * 60 * 24 * 365 },
  { label: "10 years", seconds: DEFAULT_EXPIRATION_SECONDS },
] as const;

/**
 * Roles a key can carry. COMPANY_USER is always included: the backend adds it regardless of what
 * is sent, so offering it as a choice would be a control that does nothing.
 *
 * The other two are offered to everyone and refused by the server when the caller lacks the right
 * to assign them. Hiding them client-side instead would be guessing at the caller's permissions,
 * and the guard that matters is the server's.
 */
export const ASSIGNABLE_ROLES = [Role.COMPANY_ADMIN, Role.COMPANY_ROOT];

export function rolesToSubmit(selected: string[]): string[] {
  return Array.from(new Set([...selected, Role.COMPANY_USER]));
}

/**
 * What the backend says when the signing secret is unset. Matched on substance rather than the
 * whole sentence so a reworded message still lands in the right branch.
 */
const UNCONFIGURED_MARKER = "conductor.security.jwt.secret";

export type CreateFailure =
  | { kind: "unconfigured" }
  | { kind: "forbidden" }
  | { kind: "other"; message: string };

/**
 * Classifies a failed create so the screen can explain rather than just going red.
 *
 * The unconfigured case is the one worth separating: on beta the signing secret is deliberately
 * absent, so this is expected behaviour of a correctly deployed server, and reporting it as a
 * failure would send someone looking for a bug that is not there.
 */
export function classifyCreateFailure(
  status: number | undefined,
  message: string | undefined
): CreateFailure {
  if (status === 400 && (message ?? "").includes(UNCONFIGURED_MARKER)) {
    return { kind: "unconfigured" };
  }
  if (status === 403) {
    return { kind: "forbidden" };
  }
  return {
    kind: "other",
    message: message?.trim()
      ? (message as string)
      : "The key could not be created.",
  };
}

export function describeCreateFailure(failure: CreateFailure): string {
  switch (failure.kind) {
    case "unconfigured":
      return (
        "Access keys are not enabled in this environment: no token signing secret is " +
        "configured. Existing keys can still be listed and revoked."
      );
    case "forbidden":
      return "You can only create a key with roles you are allowed to grant to a person.";
    default:
      return failure.message;
  }
}

/**
 * How to present a key's expiry.
 *
 * Most keys carry no usable value: the column defaults to -1, and on production the overwhelming
 * majority of active keys hold a non-future value. Rendering those as a date, or as "expired",
 * would both be wrong, since nothing on the server consults this field.
 */
export type ExpiryDisplay =
  | { kind: "none" }
  | { kind: "date"; at: Date }
  | { kind: "past"; at: Date };

export function expiryOf(
  key: AccessKey,
  now: Date = new Date()
): ExpiryDisplay {
  const value = key.expiresAt;
  if (value === undefined || value === null || value <= 0) {
    return { kind: "none" };
  }
  const at = new Date(value);
  return at.getTime() > now.getTime()
    ? { kind: "date", at }
    : { kind: "past", at };
}

export function formatExpiry(display: ExpiryDisplay): string {
  switch (display.kind) {
    case "none":
      return "None recorded";
    case "past":
      return `${display.at.toLocaleDateString()} (elapsed)`;
    default:
      return display.at.toLocaleDateString();
  }
}

export function formatCreated(key: AccessKey): string {
  if (!key.createdAt) {
    return "-";
  }
  const at = new Date(key.createdAt);
  return Number.isNaN(at.getTime()) ? "-" : at.toLocaleString();
}

/** Newest first, so a key just created is the one at the top. */
export function sortKeys(keys: AccessKey[]): AccessKey[] {
  return [...keys].sort((a, b) => {
    const at = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bt = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bt - at;
  });
}

/** Enough of the id to recognise a key, without a column of full UUIDs. */
export function shortId(id: string): string {
  return id.length > 13 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}
